/**
 * Валютна операція (купівля/продаж валюти) — ч.799, як 1С УТП «ПокупкаПродажаВалюты».
 * Банківські документи з видом «Купівля/продаж валюти» лягають на транзит
 * 333 (грн) / 334 (валюта); цей документ закриває транзит і пише різницю
 * курсів (942 витрати / 711 дохід) та комісію банку (92).
 * Звʼязані банк-документи підставляють суми/рахунки; курс НБУ — на дату.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { orgs } from '@/api/catalogs';
import { refs } from '@/api/refs';
import { bankPayments, bankReceipts } from '@/api/bankDocs';
import { currencyOperations, type CurrencyOperationType } from '@/api/currencyOperations';
import { usePermissionsByOrg } from '@/hooks/usePermissionsByOrg';
import { validateRequired } from '@/utils/validateRequired';
import { extractApiError } from '@/utils/extractApiError';
import DatePopover from '@/components/DatePopover';
import SearchSelect from '@/components/SearchSelect';
import DocumentToolbar from '@/shared/components/DocumentToolbar';
import AttachmentsPanel from '@/shared/components/AttachmentsPanel';
import DocLayout from '@/shared/components/DocLayout';
import DocBlock, { type DocBlockState } from '@/shared/components/DocBlock';
import DocRail from '@/shared/components/DocRail';
import { useFormDraft } from '@/shared/hooks/useFormDraft';
import { useConfirm } from '@/components/ConfirmProvider';
import TchTable from '@/shared/components/TchTable';

const PERMISSION = 'documents.currency_operations.create';
const fmt2 = (v: number) => v.toLocaleString('uk-UA', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

interface FormState {
  organization_id: string;
  document_date: string;
  operation_type: CurrencyOperationType;
  from_bank_account_id: string;
  to_bank_account_id: string;
  currency_id: string;
  currency_amount: string;
  commercial_rate: string;
  nbu_rate: string;
  bank_commission: string;
  related_bank_payment_id: string;
  related_bank_receipt_id: string;
  comment: string;
}
const EMPTY: FormState = {
  organization_id: '', document_date: new Date().toISOString().slice(0, 16),
  operation_type: 'SALE', from_bank_account_id: '', to_bank_account_id: '', currency_id: '',
  currency_amount: '', commercial_rate: '', nbu_rate: '', bank_commission: '0',
  related_bank_payment_id: '', related_bank_receipt_id: '', comment: '',
};

/** Дзеркало backend build_entries (currency_purchase_sale_posting) — для прев'ю. */
function previewEntries(f: FormState): { dt: string; cr: string; amount: number; content: string }[] {
  const cur = Number(f.currency_amount) || 0, c = Number(f.commercial_rate) || 0, n = Number(f.nbu_rate) || 0;
  const com = Number(f.bank_commission) || 0;
  if (cur <= 0 || c <= 0 || n <= 0) return [];
  const uahC = cur * c, uahN = cur * n;
  const out: { dt: string; cr: string; amount: number; content: string }[] = [];
  if (f.operation_type === 'SALE') {
    const diff = uahN - uahC;
    out.push({ dt: '333', cr: '334', amount: uahC, content: 'закриття транзиту (за комерційним курсом)' });
    if (diff > 0.005) out.push({ dt: '942', cr: '334', amount: diff, content: 'різниця курсів (витрати)' });
    else if (diff < -0.005) out.push({ dt: '334', cr: '711', amount: -diff, content: 'різниця курсів (дохід)' });
  } else {
    const diff = uahC - uahN;
    out.push({ dt: '334', cr: '333', amount: uahN, content: 'закриття транзиту (за курсом НБУ)' });
    if (diff > 0.005) out.push({ dt: '942', cr: '333', amount: diff, content: 'різниця курсів (витрати)' });
    else if (diff < -0.005) out.push({ dt: '333', cr: '711', amount: -diff, content: 'різниця курсів (дохід)' });
  }
  if (com > 0) out.push({ dt: '92', cr: '333', amount: com, content: 'комісія банку' });
  return out;
}

export default function CurrencyOperationEditor() {
  const navigate = useNavigate();
  const confirm = useConfirm();
  const qc = useQueryClient();
  const { id } = useParams();
  const [sp] = useSearchParams();
  const isNew = !id || id === 'new';
  const { availableOrgs } = usePermissionsByOrg(PERMISSION);
  const orgList = useQuery({ queryKey: ['orgs'], queryFn: () => orgs.list({ limit: 200 }) });
  const [form, setForm] = useState<FormState>(EMPTY);
  const [error, setError] = useState<string | null>(null);

  const docQuery = useQuery({
    queryKey: ['currency-operation', id], queryFn: () => currencyOperations.get(id!), enabled: !isNew,
  });
  useFormDraft(form, setForm, { ready: isNew || !!docQuery.data });
  useEffect(() => {
    if (isNew && availableOrgs.length > 0 && !form.organization_id) {
      const pref = sp.get('org');
      const opPref = sp.get('op');
      setForm((f) => ({
        ...f, organization_id: pref && availableOrgs.some((o) => o.id === pref) ? pref : availableOrgs[0]!.id,
        operation_type: opPref === 'PURCHASE' || opPref === 'SALE' ? opPref : f.operation_type,
      }));
    }
  }, [isNew, availableOrgs, form.organization_id, sp]);
  useEffect(() => {
    if (!isNew && docQuery.data) {
      const d = docQuery.data;
      setForm({
        organization_id: d.organization_id, document_date: d.document_date.slice(0, 16),
        operation_type: d.operation_type, from_bank_account_id: d.from_bank_account_id,
        to_bank_account_id: d.to_bank_account_id, currency_id: d.currency_id,
        currency_amount: d.currency_amount, commercial_rate: d.commercial_rate, nbu_rate: d.nbu_rate,
        bank_commission: d.bank_commission,
        related_bank_payment_id: d.related_bank_payment_id ?? '', related_bank_receipt_id: d.related_bank_receipt_id ?? '',
        comment: d.comment ?? '',
      });
    }
  }, [isNew, docQuery.data]);
  const isPosted = docQuery.data?.posted ?? false;

  // Довідники: рахунки організації + валюти.
  const baQ = useQuery({ queryKey: ['bank-accounts', form.organization_id], queryFn: () => refs.bankAccounts(form.organization_id), enabled: !!form.organization_id });
  const curQ = useQuery({ queryKey: ['currencies'], queryFn: refs.currencies, staleTime: 3_600_000 });
  const curCode = (cid: string) => (curQ.data ?? []).find((c) => c.id === cid)?.code ?? '';
  const accounts = baQ.data ?? [];
  const accLabel = (a: { account_name: string | null; iban: string; currency_id: string }) =>
    `${a.account_name ?? a.iban} (${curCode(a.currency_id) || '…'})`;
  const isSale = form.operation_type === 'SALE';
  // «З рахунка»: продаж — валютний, купівля — гривневий; «На рахунок» — навпаки.
  const fxAccounts = accounts.filter((a) => curCode(a.currency_id) && curCode(a.currency_id) !== 'UAH');
  const uahAccounts = accounts.filter((a) => curCode(a.currency_id) === 'UAH');
  const fromList = isSale ? fxAccounts : uahAccounts;
  const toList = isSale ? uahAccounts : fxAccounts;
  const fxAccountId = isSale ? form.from_bank_account_id : form.to_bank_account_id;
  const fxCurrencyId = accounts.find((a) => a.id === fxAccountId)?.currency_id ?? '';
  const fxCode = curCode(fxCurrencyId || form.currency_id);
  useEffect(() => {
    if (fxCurrencyId && form.currency_id !== fxCurrencyId) setForm((f) => ({ ...f, currency_id: fxCurrencyId }));
  }, [fxCurrencyId, form.currency_id]);

  // Звʼязані банк-документи виду «Купівля/продаж валюти» (обидва списки).
  const payQ = useQuery({
    queryKey: ['fx-bank-payments', form.organization_id],
    queryFn: () => bankPayments.list({ organization_id: form.organization_id, operation_type: 'CURRENCY_EXCHANGE', limit: 300 }),
    enabled: !!form.organization_id,
  });
  const recQ = useQuery({
    queryKey: ['fx-bank-receipts', form.organization_id],
    queryFn: () => bankReceipts.list({ organization_id: form.organization_id, operation_type: 'CURRENCY_EXCHANGE', limit: 300 }),
    enabled: !!form.organization_id,
  });
  // Уже привʼязані до інших валютних операцій — не пропонуємо (одна операція = один документ банку).
  const opsQ = useQuery({
    queryKey: ['currency-operations', form.organization_id, 'links'],
    queryFn: () => currencyOperations.list({ organization_id: form.organization_id, limit: 500 }),
    enabled: !!form.organization_id,
  });
  const usedPay = new Set((opsQ.data?.items ?? []).filter((o) => o.id !== id && !o.deletion_mark).map((o) => o.related_bank_payment_id).filter(Boolean));
  const usedRec = new Set((opsQ.data?.items ?? []).filter((o) => o.id !== id && !o.deletion_mark).map((o) => o.related_bank_receipt_id).filter(Boolean));
  // Продаж: списання — з ВАЛЮТНОГО рахунку, надходження — на ГРИВНЕВИЙ; купівля — навпаки.
  const payDocs = (payQ.data?.items ?? []).filter((d) => (isSale ? (d.currency_code && d.currency_code !== 'UAH') : (!d.currency_code || d.currency_code === 'UAH'))
    && (!usedPay.has(d.id) || d.id === form.related_bank_payment_id));
  const recDocs = (recQ.data?.items ?? []).filter((d) => (isSale ? (!d.currency_code || d.currency_code === 'UAH') : (d.currency_code && d.currency_code !== 'UAH'))
    && (!usedRec.has(d.id) || d.id === form.related_bank_receipt_id));
  const fmtD = (d: string) => new Date(d).toLocaleDateString('uk-UA');
  const docItem = (d: { id: string; document_number: string; document_date: string; amount: string; amount_currency?: string | null; currency_code?: string | null; bank_account_name?: string | null; posted: boolean }) => ({
    id: d.id,
    label: `№ ${d.document_number} від ${fmtD(d.document_date)} — ${d.amount_currency && d.currency_code && d.currency_code !== 'UAH' ? `${fmt2(Number(d.amount_currency))} ${d.currency_code}` : `${fmt2(Number(d.amount))} грн`}`,
    hint: `${d.bank_account_name ?? ''}${d.posted ? '' : ' · чернетка'}`,
  });
  const pickPayment = (pid: string) => {
    const d = (payQ.data?.items ?? []).find((x) => x.id === pid);
    const patch: Partial<FormState> = { related_bank_payment_id: pid };
    if (d) {
      patch.from_bank_account_id = d.bank_account_id;
      if (isSale) {
        // продаж: списання валюти → сума у валюті + НБУ з документа
        if (d.amount_currency) patch.currency_amount = d.amount_currency;
        if (d.exchange_rate) patch.nbu_rate = d.exchange_rate;
      } else if (Number(form.currency_amount) > 0) {
        // купівля: списання грн → комерційний курс = грн / валюта (без комісії)
        patch.commercial_rate = ((Number(d.amount) - (Number(form.bank_commission) || 0)) / Number(form.currency_amount)).toFixed(6);
      }
      if (!form.document_date || isNew) patch.document_date = d.document_date.slice(0, 16);
    }
    setForm((f) => ({ ...f, ...patch }));
  };
  const pickReceipt = (rid: string) => {
    const d = (recQ.data?.items ?? []).find((x) => x.id === rid);
    const patch: Partial<FormState> = { related_bank_receipt_id: rid };
    if (d) {
      patch.to_bank_account_id = d.bank_account_id;
      if (isSale) {
        // продаж: надходження грн → комерційний курс = (грн + комісія) / валюта
        if (Number(form.currency_amount) > 0) {
          patch.commercial_rate = ((Number(d.amount) + (Number(form.bank_commission) || 0)) / Number(form.currency_amount)).toFixed(6);
        }
      } else {
        if (d.amount_currency) patch.currency_amount = d.amount_currency;
        if (d.exchange_rate) patch.nbu_rate = d.exchange_rate;
      }
    }
    setForm((f) => ({ ...f, ...patch }));
  };
  // ч.930: «створити валютну операцію» з платіжки/надходження — ?bank_payment= /
  // ?bank_receipt= підставляє документ банку так само, як вибір у пікері.
  const prefilledRef = useRef(false);
  useEffect(() => {
    if (!isNew || prefilledRef.current) return;
    const bp = sp.get('bank_payment');
    const br = sp.get('bank_receipt');
    if (bp && payQ.data) {
      prefilledRef.current = true;
      if ((payQ.data.items ?? []).some((x) => x.id === bp)) pickPayment(bp);
    } else if (br && recQ.data) {
      prefilledRef.current = true;
      if ((recQ.data.items ?? []).some((x) => x.id === br)) pickReceipt(br);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isNew, sp, payQ.data, recQ.data]);
  const nbuMut = useMutation({
    mutationFn: () => currencyOperations.nbuRate(fxCode, form.document_date.slice(0, 10)),
    onSuccess: (r) => { if (r.rate) setForm((f) => ({ ...f, nbu_rate: r.rate! })); else setError(`Курс НБУ ${fxCode} на цю дату не знайдено`); },
    onError: (e) => setError(extractApiError(e)),
  });

  const entries = useMemo(() => previewEntries(form), [form]);
  const fx = useMemo(() => {
    const cur = Number(form.currency_amount) || 0, c = Number(form.commercial_rate) || 0, n = Number(form.nbu_rate) || 0;
    const uahC = cur * c, uahN = cur * n;
    const diff = isSale ? uahN - uahC : uahC - uahN;   // >0 витрати, <0 дохід
    let warn: string | null = null;
    if (n > 0 && c > 0 && (c < n * 0.9 || c > n * 1.1)) warn = 'Комерційний курс відрізняється від НБУ більш ніж на 10% — перевірте';
    return { uahC, uahN, diff, warn };
  }, [form.currency_amount, form.commercial_rate, form.nbu_rate, isSale]);

  const payload = () => ({
    document_date: new Date(form.document_date).toISOString(),
    operation_type: form.operation_type,
    from_bank_account_id: form.from_bank_account_id, to_bank_account_id: form.to_bank_account_id,
    currency_id: form.currency_id, currency_amount: form.currency_amount,
    commercial_rate: form.commercial_rate, nbu_rate: form.nbu_rate,
    bank_commission: form.bank_commission || '0',
    related_bank_payment_id: form.related_bank_payment_id || null,
    related_bank_receipt_id: form.related_bank_receipt_id || null,
    comment: form.comment || null,
  });
  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ['currency-operations'] });
    qc.invalidateQueries({ queryKey: ['currency-operation', id] });
  };
  const create = useMutation({
    mutationFn: () => currencyOperations.create({ organization_id: form.organization_id, ...payload() }),
    onSuccess: (doc) => { invalidate(); navigate(`/banking/currency-operations/${doc.id}`); },
    onError: (e) => setError(extractApiError(e)),
  });
  const update = useMutation({
    mutationFn: () => currencyOperations.update(id!, payload()),
    onSuccess: invalidate, onError: (e) => setError(extractApiError(e)),
  });
  const post = useMutation({
    mutationFn: async () => {
      if (isNew) {
        const d = await currencyOperations.create({ organization_id: form.organization_id, ...payload() });
        try { await currencyOperations.post(d.id); } catch (e) {
          // ч.1343: новий документ уже створено — відкриваємо його, інакше повторне «провести» створило б дубль.
          navigate(`/banking/currency-operations/${d.id}`, { replace: true });
          throw e;
        }
        return d.id;
      }
      await currencyOperations.update(id!, payload()); await currencyOperations.post(id!); return id!;
    },
    onSuccess: (docId) => { invalidate(); if (isNew) navigate(`/banking/currency-operations/${docId}`); },
    onError: (e) => setError(extractApiError(e)),
  });
  const unpost = useMutation({
    mutationFn: () => currencyOperations.unpost(id!), onSuccess: invalidate, onError: (e) => setError(extractApiError(e)),
  });
  const remove = useMutation({
    mutationFn: () => currencyOperations.remove(id!),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['currency-operations'] }); navigate('/banking/currency-operations'); },
    onError: (e) => setError(extractApiError(e)),
  });
  const validate = (): boolean => {
    setError(null);
    const err = validateRequired(
      { organization_id: form.organization_id, from_bank_account_id: form.from_bank_account_id, to_bank_account_id: form.to_bank_account_id,
        currency_id: form.currency_id, currency_amount: form.currency_amount, commercial_rate: form.commercial_rate, nbu_rate: form.nbu_rate },
      { organization_id: 'Організація', from_bank_account_id: 'З рахунка', to_bank_account_id: 'На рахунок',
        currency_id: 'Валюта', currency_amount: 'Сума у валюті', commercial_rate: 'Курс комерційний', nbu_rate: 'Курс НБУ' },
    );
    if (err) { setError(err); return false; }
    if (form.from_bank_account_id === form.to_bank_account_id) { setError('Рахунки «З» і «На» мають різнитися'); return false; }
    return true;
  };

  const pending = create.isPending || update.isPending || post.isPending;
  // ч.870 (новий дизайн, Закон #10): «ще дій» не було — права колонка без
  // «Повʼязаних дій». Прогрес — лише обовʼязкові (ті, що validate() вимагає).
  const railActions: { label: string; title?: string; disabled?: boolean; onClick: () => void }[] = [];
  const blocks: DocBlockState[] = [
    { id: 'main', title: 'Основні дані', required: 3,
      filled: (form.organization_id ? 1 : 0) + (form.document_date ? 1 : 0) + (form.operation_type ? 1 : 0) },
    { id: 'accounts', title: 'Банківські документи і рахунки', required: 2,
      filled: (form.from_bank_account_id ? 1 : 0) + (form.to_bank_account_id ? 1 : 0) },
    { id: 'amounts', title: 'Сума і курси', required: 3,
      filled: (Number(form.currency_amount) > 0 ? 1 : 0) + (Number(form.commercial_rate) > 0 ? 1 : 0)
        + (Number(form.nbu_rate) > 0 ? 1 : 0) },
    { id: 'extra', title: 'Коментарі та вкладення', required: 0, filled: 0 },
  ];

  return (
    <div className="card">
      <div className="toolbar">
        <h2 style={{ margin: 0 }}>
          {isNew ? 'Нова валютна операція' : `Валютна операція ${docQuery.data?.document_number ?? ''}`}
        </h2>
        <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center',
          flexWrap: 'wrap', marginLeft: 'auto' }}>
        <DocumentToolbar
          onBackToList={() => navigate('/banking/currency-operations')}
          onSave={() => { if (validate()) (isNew ? create.mutate() : update.mutate()); }}
          saveDisabled={pending}
          isPosted={isPosted}
          isNew={isNew}
          onPost={() => { if (validate()) post.mutate(); }}
          postPending={post.isPending}
          onUnpost={() => unpost.mutate()}
          unpostPending={unpost.isPending}
          onDelete={() => { void confirm('Видалити документ?').then((ok) => { if (ok) remove.mutate(); }); }}
          deletePending={remove.isPending}
          entriesDoc={{ type: 'doc.currency_purchase_sale', id: isNew ? '' : id! }}
          railMode
          extraActions={railActions}
        />
        </div>
      </div>

      <DocLayout rail={
        <DocRail blocks={blocks} actions={railActions} isNew={isNew}
          doc={{ type: 'doc.currency_purchase_sale', id: isNew ? '' : id! }} />
      }>
      <DocBlock index={1} title="Основні дані">
      {/* Рядок 1: Організація | Дата | Тип */}
      <div className="doc-grid">
        <div className="field" style={{ gridColumn: 'span 5' }}>
          <label>Організація *</label>
          {isNew ? (
            <select value={form.organization_id} onChange={(e) => setForm({ ...form, organization_id: e.target.value, from_bank_account_id: '', to_bank_account_id: '', related_bank_payment_id: '', related_bank_receipt_id: '' })}>
              <option value="">—</option>
              {availableOrgs.map((o) => <option key={o.id} value={o.id}>{o.name} — {o.code}</option>)}
            </select>
          ) : (
            <div style={{ paddingTop: 6 }}>{orgList.data?.items.find((o) => o.id === form.organization_id)?.name ?? ''}</div>
          )}
        </div>
        <div className="field" style={{ gridColumn: 'span 3' }}>
          <label>Дата *</label>
          <DatePopover withTime value={form.document_date} disabled={isPosted} onChange={(v) => setForm({ ...form, document_date: v })} />
        </div>
        <div className="field" style={{ gridColumn: 'span 4' }}>
          <label>Операція *</label>
          <select value={form.operation_type} disabled={isPosted}
            onChange={(e) => setForm({ ...form, operation_type: e.target.value as CurrencyOperationType, from_bank_account_id: '', to_bank_account_id: '', related_bank_payment_id: '', related_bank_receipt_id: '' })}>
            <option value="SALE">Продаж валюти (валюта → грн)</option>
            <option value="PURCHASE">Купівля валюти (грн → валюта)</option>
          </select>
        </div>
      </div>

      </DocBlock>

      <DocBlock index={2} title="Банківські документи і рахунки">
      {/* Рядок 2: банк-документи «купівля/продаж валюти» → рахунки */}
      <div className="doc-grid">
        <div className="field" style={{ gridColumn: 'span 12', margin: 0, fontSize: '0.82rem', color: 'var(--muted)' }}>
          {isSale
            ? 'Продаж: банк списує валюту з вашого рахунку на продаж (документ списання з виписки, Дт 334 Кт 312) і зараховує гривню (документ надходження, Дт 311 Кт 333). Це НЕ ті платіжки, якими валюта надійшла на рахунок — з якого надходження продавати, значення не має. Продали двічі (9 000 і 2 000) — дві операції з двома парами документів.'
            : 'Купівля: банк списує гривню з вашого рахунку (документ списання, Дт 333 Кт 311) і зараховує валюту (документ надходження з виписки, Дт 312 Кт 334).'}
        </div>
        <div className="field" style={{ gridColumn: 'span 6' }}>
          <label>{isSale ? 'Списання валюти на продаж (з валютного рахунку)' : 'Списання гривні на купівлю (з гривневого рахунку)'}</label>
          <SearchSelect items={payDocs.map(docItem)} value={form.related_bank_payment_id}
            onChange={pickPayment} disabled={isPosted} placeholder="списання: оберіть або залиште порожнім…" />
        </div>
        <div className="field" style={{ gridColumn: 'span 6' }}>
          <label>{isSale ? 'Надходження гривні від продажу (на гривневий рахунок)' : 'Надходження купленої валюти (на валютний рахунок)'}</label>
          <SearchSelect items={recDocs.map(docItem)} value={form.related_bank_receipt_id}
            onChange={pickReceipt} disabled={isPosted} placeholder="надходження: оберіть або залиште порожнім…" />
        </div>
        <div className="field" style={{ gridColumn: 'span 6' }}>
          <label>З рахунка *</label>
          <select value={form.from_bank_account_id} disabled={isPosted} onChange={(e) => setForm({ ...form, from_bank_account_id: e.target.value })}>
            <option value="">—</option>
            {fromList.map((a) => <option key={a.id} value={a.id}>{accLabel(a)}</option>)}
          </select>
        </div>
        <div className="field" style={{ gridColumn: 'span 6' }}>
          <label>На рахунок *</label>
          <select value={form.to_bank_account_id} disabled={isPosted} onChange={(e) => setForm({ ...form, to_bank_account_id: e.target.value })}>
            <option value="">—</option>
            {toList.map((a) => <option key={a.id} value={a.id}>{accLabel(a)}</option>)}
          </select>
        </div>
      </div>

      </DocBlock>

      <DocBlock index={3} title="Сума і курси">
      {/* Рядок 3: сума у валюті | курс комерційний | курс НБУ | комісія */}
      <div className="doc-grid">
        <div className="field" style={{ gridColumn: 'span 3' }}>
          <label>Сума у валюті{fxCode ? ` (${fxCode})` : ''} *</label>
          <input type="number" step="0.01" value={form.currency_amount} disabled={isPosted}
            onChange={(e) => setForm({ ...form, currency_amount: e.target.value })} />
        </div>
        <div className="field" style={{ gridColumn: 'span 3' }}>
          <label>Курс комерційний *</label>
          <input type="number" step="0.0001" value={form.commercial_rate} disabled={isPosted}
            title={isSale ? 'За скільки грн банк купив 1 одиницю валюти' : 'За скільки грн куплено 1 одиницю валюти'}
            onChange={(e) => setForm({ ...form, commercial_rate: e.target.value })} />
        </div>
        <div className="field" style={{ gridColumn: 'span 3' }}>
          <label>Курс НБУ на дату *</label>
          <div style={{ display: 'flex', gap: 4 }}>
            <input type="number" step="0.0001" value={form.nbu_rate} disabled={isPosted} style={{ flex: 1 }}
              onChange={(e) => setForm({ ...form, nbu_rate: e.target.value })} />
            <button type="button" disabled={isPosted || !fxCode || nbuMut.isPending} onClick={() => nbuMut.mutate()}
              title="Підтягнути курс НБУ на дату документа">{nbuMut.isPending ? '…' : 'НБУ'}</button>
          </div>
        </div>
        <div className="field" style={{ gridColumn: 'span 3' }}>
          <label>Комісія банку, грн</label>
          <input type="number" step="0.01" value={form.bank_commission} disabled={isPosted}
            onChange={(e) => setForm({ ...form, bank_commission: e.target.value })} />
        </div>
      </div>
      {/* Розрахунок і проводки закриття транзиту */}
      <div style={{ marginTop: '0.5rem', marginBottom: '0.6rem', padding: '0.6rem 0.75rem', background: 'var(--bg-soft-2)', border: '1px solid var(--border)', borderRadius: 6, fontSize: '0.9rem' }}>
        <div style={{ display: 'flex', gap: '1.5rem', flexWrap: 'wrap' }}>
          <span>За комерційним: <b>{fmt2(fx.uahC)} грн</b></span>
          <span>За НБУ: <b>{fmt2(fx.uahN)} грн</b></span>
          <span style={{ color: fx.diff > 0.005 ? '#b91c1c' : fx.diff < -0.005 ? '#15803d' : undefined }}>
            Різниця курсів: <b>{fmt2(Math.abs(fx.diff))} грн</b> {fx.diff > 0.005 ? '(витрати, 942)' : fx.diff < -0.005 ? '(дохід, 711)' : ''}
          </span>
        </div>
        {fx.warn && <div style={{ marginTop: 4, color: 'var(--badge-amber-fg)' }}>⚠ {fx.warn}</div>}
        {entries.length > 0 && (
          <TchTable style={{ marginTop: '0.5rem', maxWidth: 720 }}>
            <thead><tr><th style={{ width: 60 }}>Дт</th><th style={{ width: 60 }}>Кт</th><th className="num" style={{ width: 140 }}>Сума, грн</th><th>Зміст</th></tr></thead>
            <tbody>
              {entries.map((e, i) => (
                <tr key={i}><td>{e.dt}</td><td>{e.cr}</td><td className="num">{fmt2(e.amount)}</td><td>{e.content}</td></tr>
              ))}
            </tbody>
          </TchTable>
        )}
        <div style={{ marginTop: 6, color: 'var(--muted)', fontSize: '0.8rem' }}>
          Рух грошей дають самі банківські документи: {isSale
            ? 'списання валюти Дт 334 Кт 312 (за НБУ) і надходження грн Дт 311 Кт 333'
            : 'списання грн Дт 333 Кт 311 і надходження валюти Дт 312 Кт 334 (за НБУ)'}. Цей документ закриває транзит.
        </div>
      </div>

      </DocBlock>

      <DocBlock index={4} title="Коментарі та вкладення">
      <div className="docblock__cols">
        <div className="field" style={{ margin: 0 }}>
          <label>Коментар</label>
          <textarea rows={2} value={form.comment} disabled={isPosted} onChange={(e) => setForm({ ...form, comment: e.target.value })} />
        </div>
        <div>
          <AttachmentsPanel organizationId={form.organization_id} documentType="doc.currency_purchase_sale" documentId={isNew ? undefined : id} />
        </div>
      </div>
      </DocBlock>
      </DocLayout>
      {error && <div className="error" style={{ marginTop: '0.75rem' }}>{error}</div>}
    </div>
  );
}
